/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'
import { capitalize } from 'lodash'
import { FormattedDate } from 'react-intl'

import { EuiCallOut, EuiSpacer } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import useOrganization from '@modules/billing-lib/hooks/useOrganization'
import { getSubscriptionChangeEventType } from '@modules/billing-lib/utils'

// eslint-disable-next-line import/no-restricted-paths
import { numericDateTime } from '@/config/dates'
// eslint-disable-next-line import/no-restricted-paths
import ExternalLink from '@/components/ExternalLink'
// eslint-disable-next-line import/no-restricted-paths
import { externalPricing } from '@/apps/userconsole/urls'

const PendingSubscriptionUpdateNotice = () => {
  const orgQuery = useOrganization()

  if (!orgQuery.data || !orgQuery.data.subscription.pending_level) {
    return null
  }

  return (
    <Fragment>
      <EuiSpacer size='m' />
      <EuiCallOut
        size='s'
        title={
          <FormattedMessage
            id='pending-subscription-update-notice.message'
            defaultMessage='Your subscription is currently pending an {changeType} to {pending_level}. This change becomes effective on {pending_level_valid_from}. For more information about the subscription tiers, please visit our <pricingLink>pricing page</pricingLink>.'
            values={{
              pending_level: capitalize(orgQuery.data.subscription.pending_level),
              pending_level_valid_from: (
                <FormattedDate
                  data-test-id='expiration-date-column-value'
                  value={orgQuery.data.subscription.pending_level_valid_from}
                  {...numericDateTime}
                />
              ),
              pricingLink: (content) => (
                <ExternalLink showExternalLinkIcon={false} href={externalPricing}>
                  <strong>{content}</strong>
                </ExternalLink>
              ),
              changeType: {
                upgrade: (
                  <FormattedMessage
                    id='subscription-change.event-type.upgrade'
                    defaultMessage='upgrade'
                  />
                ),
                downgrade: (
                  <FormattedMessage
                    id='subscription-change.event-type.downgrade'
                    defaultMessage='downgrade'
                  />
                ),
              }[
                getSubscriptionChangeEventType(
                  orgQuery.data.subscription.level,
                  orgQuery.data.subscription.pending_level,
                )
              ],
            }}
          />
        }
        iconType='clock'
        data-test-id='pending-subscription-update-notice'
      />
    </Fragment>
  )
}

export default PendingSubscriptionUpdateNotice
